import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import LeadFormModal from './LeadFormModal';

const { FiPlus, FiLayout, FiCode, FiTrash2, FiExternalLink, FiCopy, FiCheck } = FiIcons;

const LeadForms = () => {
  const { leadForms, deleteLeadForm } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const copyEmbedCode = (id) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const embedCode = `<iframe src="${baseUrl}#/public/form/${id}" width="100%" height="600px" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Lead Capture Forms</h1>
          <p className="text-slate-400 font-medium">Create embeddable forms to capture leads into your CRM.</p>
        </div>
        <button 
          onClick={() => { setEditingForm(null); setIsModalOpen(true); }}
          className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all"
        >
          <SafeIcon icon={FiPlus} /> Create Form
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {leadForms.map(form => (
          <div key={form.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
            <div className="flex justify-between items-start mb-6">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                <SafeIcon icon={FiLayout} className="text-2xl" />
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={() => copyEmbedCode(form.id)}
                  className={`p-2 rounded-xl transition-all ${copiedId === form.id ? 'bg-green-50 text-green-600' : 'text-slate-300 hover:text-blue-600 hover:bg-blue-50'}`}
                  title="Copy Embed Code"
                >
                  <SafeIcon icon={copiedId === form.id ? FiCheck : FiCode} />
                </button>
                <button 
                  onClick={() => deleteLeadForm(form.id)}
                  className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            </div>
            
            <h3 className="text-xl font-black text-slate-900 mb-2">{form.name}</h3>
            <p className="text-slate-400 text-sm font-medium mb-8 line-clamp-2">{form.description || 'No description'}</p>
            
            <div className="pt-6 border-t border-slate-50 flex items-center justify-between">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {form.fields?.length || 0} Fields
              </span>
              <a 
                href={`#/public/form/${form.id}`} 
                target="_blank" 
                rel="noreferrer"
                className="text-blue-600 font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:underline"
              >
                Preview <SafeIcon icon={FiExternalLink} />
              </a>
            </div>
          </div>
        ))}
        
        {leadForms.length === 0 && (
          <div className="col-span-full py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-100">
            <SafeIcon icon={FiLayout} className="text-6xl text-slate-100 mx-auto mb-4" />
            <p className="text-slate-400 font-bold">No lead forms created yet.</p>
          </div>
        )}
      </div>

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} form={editingForm} />
    </div>
  );
};

export default LeadForms;